import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { VonPageResponseModel } from '@von-development-studio/angular-rest-service';
import { VonMessageService } from '@von-development-studio/primeng-message-service';
import { FilterMetadata, SelectItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { BaseService } from 'src/app/core/base/base.service';
import { RestService } from 'src/app/core/service/rest.service';
import { AdminCatalogModel } from 'src/app/model/admin/catalog';
import { CommonPersonModel } from 'src/app/model/common/person';
import { CommonPersonViewModel } from 'src/app/model/common/person.view';
import { KhuyanaSelectItemModel } from 'src/app/model/core/khuyana-select-item.model';
import { commonPersonActions } from 'src/app/store/common/person/person.action';
import { environment } from 'src/environments/environment';
import { AdminLocationService } from '../../admin/location/admin-location.service';
import { AdminSubCatalogService } from '../../admin/subcatalog/admin-subcatalog.service';
import { PERSON } from './person.constants';

@Injectable({
  providedIn: 'root',
})
export class PersonService extends BaseService {
  readonly screen = PERSON;

  constructor(
    protected store: Store<any>,
    protected rest: RestService,
    protected dialogService: DialogService,
    protected messageService: VonMessageService,
    protected subCatalogService: AdminSubCatalogService,
    protected locationService: AdminLocationService
  ) {
    super();
  }

  readStore = () => {
    const documentTypes = this.subCatalogService.getSubCatalogStore('doc_type');
    const genders = this.subCatalogService.getSubCatalogStore('gender');
    const maritalStatus =
      this.subCatalogService.getSubCatalogStore('mar_status');
    const locations: Observable<SelectItem<KhuyanaSelectItemModel>[]> =
      this.store.select('admin', 'location', 'queryResult');
    const personData: Observable<CommonPersonModel> = this.store.select(
      'common',
      'person',
      'data'
    );
    const locationBorn: Observable<SelectItem<KhuyanaSelectItemModel>> =
      this.store.select('common', 'person', 'locationBorn');
    return {
      documentTypes,
      genders,
      maritalStatus,
      locations,
      personData,
      locationBorn,
    };
  };

  readSubCatalogStore = (code: string): Observable<AdminCatalogModel[]> =>
    this.subCatalogService.getSubCatalogStore(code);

  readLocations = (query: string) =>
    this.locationService.readLocationSelectors(query);

  readPersonLazyLoad = (): Observable<
    VonPageResponseModel<CommonPersonViewModel>
  > => this.store.select('common', 'person', 'result');

  getPersonUnique = (type: string, document: string, id?: number) =>
    this.rest
      .get<CommonPersonModel>({
        url: `${environment.endpoints.common}/person/unique`,
        params: { type, document },
      })
      .pipe(
        tap((person) => {
          if (person) {
            this.setPerson(person, type, document);
          }
        })
      )
      .subscribe();

  getPersonById = (id: number) =>
    this.rest
      .get<CommonPersonModel>({
        url: `${environment.endpoints.common}/person/${id}`,
      })
      .pipe(tap((person) => this.setPerson(person)));

  getPeople = (
    page: number,
    size: number,
    nameQuery?: FilterMetadata,
    documentQuery?: FilterMetadata
  ): Observable<VonPageResponseModel<CommonPersonViewModel>> => {
    const body: any = { page, size };
    if (nameQuery) {
      body.nameQuery = nameQuery.value;
    }
    if (documentQuery) {
      body.documentQuery = documentQuery.value;
    }

    return this.rest
      .get<VonPageResponseModel<CommonPersonViewModel>>({
        url: `${environment.endpoints.common}/person`,
        params: body,
      })
      .pipe(
        tap((peopleResult) =>
          this.store.dispatch(commonPersonActions.setResultAction(peopleResult))
        )
      );
  };

  setPerson = (
    person?: CommonPersonModel,
    type?: string,
    document?: string
  ) => {
    if (person) {
      this.store.dispatch(
        commonPersonActions.setPersonAction({
          ...person,
          isNew: false,
        })
      );
      if (person.locationBorn) {
        this.rest
          .get<KhuyanaSelectItemModel>({
            url: `${environment.endpoints.admin}/location/selector/${person.locationBorn}`,
          })
          .subscribe((location) =>
            this.store.dispatch(
              commonPersonActions.setLocationBornAction({
                label: location.name,
                value: location,
              })
            )
          );
      } else {
        this.store.dispatch(
          commonPersonActions.setLocationBornAction({ value: {} })
        );
      }
    } else {
      this.store.dispatch(
        commonPersonActions.setPersonAction({
          documentType: type,
          document,
          emailHelp: [{ value: '' }],
          phoneHelp: [{ value: '' }],
          isNew: true,
        })
      );
    }
  };

  setPersonInformation = (person: CommonPersonModel): CommonPersonModel => {
    if (person.isNew) {
      return { ...person };
    }

    return {
      ...person,
      creationDate: person.creationDate
        ? new Date(person.creationDate)
        : undefined,
      updateDate: person.updateDate ? new Date(person.updateDate) : undefined,
      dateBorn: person.dateBorn
        ? new Date(`${person.dateBorn}T00:00`)
        : undefined,
      emailHelp: person.email
        ? person.email.split('|').map((email: string) => ({ value: email }))
        : [{ value: '' }],
      phoneHelp: person.phone
        ? person.phone.split('|').map((phone: string) => ({ value: phone }))
        : [{ value: '' }],
    };
  };

  save = (person: CommonPersonModel): Observable<CommonPersonViewModel> =>
    this.rest
      .post<CommonPersonViewModel>({
        url: `${environment.endpoints.common}/person`,
        body: person,
      })
      .pipe(
        tap(() =>
          this.messageService.addSuccess(this.screen.labels.saveSuccess)
        )
      );

  changeStatus = (id: number): Observable<CommonPersonViewModel> =>
    this.rest
      .patch<CommonPersonViewModel>({
        url: `${environment.endpoints.common}/person/status/${id}`,
      })
      .pipe(
        tap((person) =>
          this.store.dispatch(commonPersonActions.patchResultAction(person))
        )
      );

  openPersonDialog = (isNew: boolean = true, component: any) => {
    if (isNew) {
      this.setPerson();
    }
    const dialogRef = this.dialogService.open(component, {
      data: {
        save: this.save,
      },
      header: isNew ? this.screen.actions.add : this.screen.actions.edit,
      closeOnEscape: true,
      width: '75%',
    });
    dialogRef.onClose.subscribe((modalResult) => {
      if (modalResult) {
        const action = modalResult.isNew
          ? commonPersonActions.addResultAction
          : commonPersonActions.patchResultAction;
        this.store.dispatch(action(modalResult));
      }
      commonPersonActions.setPersonDefaultAction(true);
    });
  };
}
